import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { CheckCircle2, Circle, Loader2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { SecretInput } from '@/Domain/components/Shared/Input/SecretInput';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

const TELEGRAM_INTEGRATION_NAME = 'telegram' as const;
const TELEGRAM_QUERY_KEY = [QueryKey.integration.get, TELEGRAM_INTEGRATION_NAME] as const;
const BOTFATHER_URL = 'https://t.me/botfather';
const LINK_CLASS =
  'font-medium text-foreground underline underline-offset-2 transition-colors hover:text-muted-foreground';

interface TelegramIntegrationParams {
  telegram_bot_active: boolean;
  telegram_bot_token: string;
}

interface TelegramIntegrationResponse {
  name: typeof TELEGRAM_INTEGRATION_NAME;
  params: TelegramIntegrationParams;
}

const telegramFormSchema = z.object({
  telegram_bot_active: z.boolean(),
  telegram_bot_token: z.string(),
});

type TelegramFormValues = z.infer<typeof telegramFormSchema>;

interface TelegramSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TelegramSheet = ({ open, onOpenChange }: TelegramSheetProps) => {
  const { t } = useTranslation(['integration']);
  const queryClient = useQueryClient();
  const { capture } = usePostHogAnalytics();

  const { data, isSuccess } = useQuery<TelegramIntegrationResponse>({
    queryKey: TELEGRAM_QUERY_KEY,
    enabled: open,
  });

  const params = data?.params;

  // Prefilled with the saved token (revealable via the SecretInput eye), mirroring
  // the Discord sheet. Clearing the field and saving removes the token.
  const initialValues = useMemo<TelegramFormValues>(
    () => ({
      telegram_bot_active: params?.telegram_bot_active ?? false,
      telegram_bot_token: params?.telegram_bot_token ?? '',
    }),
    [params],
  );

  const form = useForm<TelegramFormValues>({
    resolver: zodResolver(telegramFormSchema),
    defaultValues: initialValues,
    values: initialValues,
  });

  useEffect(() => {
    if (open) {
      capture(PostHogEventEnum.TelegramIntegrationSheetOpened);
    }
  }, [open, capture]);

  const hasExistingToken = Boolean(params?.telegram_bot_token);

  // Connected = the bot is enabled and has a token; anything else reads as off.
  const isConnected = Boolean(params?.telegram_bot_active && params?.telegram_bot_token);

  const mutation = useMutation<
    TelegramIntegrationResponse,
    AxiosError<{ detail?: string }>,
    { name: typeof TELEGRAM_INTEGRATION_NAME; params: Partial<TelegramIntegrationParams> }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (response) => {
      queryClient.setQueryData(TELEGRAM_QUERY_KEY, (prev?: TelegramIntegrationResponse) => ({
        name: TELEGRAM_INTEGRATION_NAME,
        params: {
          ...(prev?.params ?? { telegram_bot_active: false, telegram_bot_token: '' }),
          ...response.params,
        },
      }));
      toast.success(t('integration:telegram.saved'));
    },
    onError: (error) => {
      // Surface the backend `detail` when present; never log the token itself.
      const detail = error.response?.data?.detail;
      capture(PostHogEventEnum.TelegramIntegrationSaveFailed, { reason: detail ?? 'unknown' });
      toast.error(detail ?? t('integration:telegram.save_failed'));
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    const changed: Partial<TelegramIntegrationParams> = {};
    const storedToken = params?.telegram_bot_token ?? '';
    // Trim so pasted whitespace from a chat/doc copy isn't stored verbatim.
    const trimmedToken = values.telegram_bot_token.trim();

    // Prefilled with the saved token: clearing it removes the token; a different
    // non-empty value replaces it; an unchanged value is left alone.
    let tokenRemoved = false;
    let tokenChanged = false;
    if (trimmedToken !== storedToken) {
      changed.telegram_bot_token = trimmedToken;
      if (trimmedToken.length === 0) {
        tokenRemoved = true;
      } else {
        tokenChanged = true;
      }
    }

    // A bot can't run without a token, so removing it also disables the bot.
    const nextActive = tokenRemoved ? false : values.telegram_bot_active;
    if (nextActive !== (params?.telegram_bot_active ?? false)) {
      changed.telegram_bot_active = nextActive;
    }

    if (Object.keys(changed).length === 0) {
      return;
    }

    mutation.mutate(
      { name: TELEGRAM_INTEGRATION_NAME, params: changed },
      {
        onSuccess: () => {
          capture(PostHogEventEnum.TelegramIntegrationSaved, {
            bot_active: nextActive,
            token_changed: tokenChanged,
            token_removed: tokenRemoved,
          });
        },
      },
    );
  });

  const canSave = form.formState.isDirty && !mutation.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="max-w-3xl w-full h-full" data-testid="telegram-sheet">
        <SheetHeader className="px-0.5">
          <SheetTitle data-testid="telegram-sheet-title">
            {t('integration:telegram.title')}
          </SheetTitle>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="telegram-close-button"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 space-y-6">
          <p className="text-sm text-muted-foreground" data-testid="telegram-description">
            {t('integration:telegram.description')}
          </p>

          {!isSuccess ? (
            <div className="flex items-center justify-center py-12" data-testid="telegram-loading">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-6">
              <div className="flex items-center gap-2" data-testid="telegram-status">
                {isConnected ? (
                  <>
                    <Badge
                      variant="success"
                      size="md"
                      startIcon={<CheckCircle2 className="size-3" />}
                      data-testid="telegram-status-connected"
                    >
                      {t('integration:telegram.status.connected')}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {t('integration:telegram.status.connected_description')}
                    </span>
                  </>
                ) : (
                  <>
                    <Badge
                      variant="secondary"
                      size="md"
                      startIcon={<Circle className="size-3" />}
                      data-testid="telegram-status-disabled"
                    >
                      {t('integration:telegram.status.disabled')}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {t('integration:telegram.status.disabled_description')}
                    </span>
                  </>
                )}
              </div>

              <div className="overflow-hidden rounded-xl border border-border bg-card divide-y divide-border">
                {/* Enable Telegram */}
                <div className="flex items-center justify-between gap-4 p-4">
                  <div className="flex-1">
                    <Label
                      htmlFor="telegram-enable-switch"
                      className="cursor-pointer text-base font-semibold"
                    >
                      {t('integration:telegram.enable')}
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('integration:telegram.hints.enable')}
                    </p>
                  </div>
                  <Controller
                    control={form.control}
                    name="telegram_bot_active"
                    render={({ field }) => (
                      <Switch
                        id="telegram-enable-switch"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="telegram-enable-switch"
                        className="scale-150"
                      />
                    )}
                  />
                </div>

                {/* Bot Token */}
                <div className="space-y-1.5 p-4">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="telegram-bot-token-input">
                      {t('integration:telegram.bot_token.label')}
                    </Label>
                    {hasExistingToken && (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium text-success"
                        data-testid="telegram-bot-token-saved-badge"
                      >
                        <CheckCircle2 className="size-3.5" />
                        {t('integration:telegram.bot_token.saved')}
                      </span>
                    )}
                  </div>
                  <Controller
                    control={form.control}
                    name="telegram_bot_token"
                    render={({ field }) => (
                      <SecretInput
                        id="telegram-bot-token-input"
                        placeholder={
                          hasExistingToken
                            ? t('integration:telegram.bot_token.replace_placeholder')
                            : t('integration:telegram.bot_token.placeholder')
                        }
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        data-testid="telegram-bot-token-input"
                        className="font-mono"
                      />
                    )}
                  />
                  <p
                    className="text-xs text-muted-foreground"
                    data-testid="telegram-bot-token-hint"
                  >
                    <Trans
                      t={t}
                      i18nKey="integration:telegram.hints.token"
                      components={[
                        <a
                          key="botfather"
                          href={BOTFATHER_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={LINK_CLASS}
                        />,
                      ]}
                    />
                  </p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="dark"
                  disabled={!canSave}
                  data-testid="telegram-save-button"
                >
                  {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
                  {mutation.isPending
                    ? t('integration:telegram.saving')
                    : t('integration:telegram.save')}
                </Button>
              </div>
            </form>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
